import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, SparkleIcon as Sparkle, MagnifyingGlassIcon as MagnifyingGlass, PencilSimpleIcon as PencilSimple, CheckIcon as Check, XIcon as CloseIcon } from "@phosphor-icons/react";

/** Admin: manage the breed catalog.
 *
 *  Add custom exotics, edit any row's description / origin / exotic flag,
 *  delete custom rows. Seed rows are protected from deletion at the
 *  backend — admins can edit them but not wipe them.
 */
export default function AdminBreedsCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [exoticOnly, setExoticOnly] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    group: "",
    origin: "",
    exotic: false,
    description: "",
  });
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingId, setSavingId] = useState(null);

  const refresh = () => {
    setLoading(true);
    api
      .get("/breeds/catalog")
      .then((r) => setRows(r.data?.breeds || []))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (exoticOnly && !r.exotic) return false;
      if (lower && !r.name.toLowerCase().includes(lower)) return false;
      return true;
    });
  }, [rows, exoticOnly, q]);

  const create = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/breeds/catalog", {
        ...draft,
        name: draft.name.trim(),
        group: (draft.group || (draft.exotic ? "Exotic" : "Non-Sporting")).trim(),
        origin: draft.origin.trim(),
        description: draft.description.trim(),
      });
      setRows((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setDraft({ name: "", group: "", origin: "", exotic: false, description: "" });
      toast.success(`Added "${data.name}".`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add breed.");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditDraft({
      group: r.group || "",
      origin: r.origin || "",
      exotic: !!r.exotic,
      description: r.description || "",
    });
  };

  const saveEdit = async (id) => {
    setSavingId(id);
    try {
      const { data } = await api.put(`/admin/breeds/catalog/${id}`, editDraft);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      setEditId(null);
      toast.success("Saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (r) => {
    if (r.is_seed) {
      toast.error("Seed breeds can't be deleted (only edited).");
      return;
    }
    if (!window.confirm(`Delete "${r.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/breeds/catalog/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success(`Removed "${r.name}".`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
          Admin · Catalog
        </p>
        <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Breed catalog
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Full registry of purebreds and exotic breeds. Add custom
          purebreds or exotics when a breeder asks for a breed we haven't
          listed yet. Seed breeds can't be deleted, only edited.
        </p>
      </div>

      {/* Add new */}
      <section
        className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-8"
        data-testid="catalog-add-card"
      >
        <h2 className="font-spectral text-xl mb-4 flex items-center gap-2">
          <Plus size={18} weight="bold" className="text-gold" />
          Add a custom breed
        </h2>

        {/* Kind toggle — purebred vs exotic. Switching auto-sets a
            sensible default group for admins who don't want to fuss
            with the dropdown. */}
        <div className="flex gap-2 mb-5" data-testid="catalog-new-kind">
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                exotic: false,
                group: d.group && d.group !== "Exotic" ? d.group : "Non-Sporting",
              }))
            }
            className={`flex-1 rounded-xl border px-4 py-3 text-left transition-colors ${
              !draft.exotic
                ? "border-[#0E0E0E] bg-[#0E0E0E] text-white"
                : "border-[#E5E5E5] bg-white text-[#3F3F3F] hover:border-[#0E0E0E]"
            }`}
            data-testid="catalog-new-kind-purebred"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold">
              Purebred
            </div>
            <div className={`text-xs mt-0.5 ${!draft.exotic ? "text-white/70" : "text-[#6B6B6B]"}`}>
              Established breed recognized by a major registry.
            </div>
          </button>
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({ ...d, exotic: true, group: "Exotic" }))
            }
            className={`flex-1 rounded-xl border px-4 py-3 text-left transition-colors ${
              draft.exotic
                ? "border-[#C9A227] bg-[#FBF7EC] text-[#8A6A00]"
                : "border-[#E5E5E5] bg-white text-[#3F3F3F] hover:border-[#C9A227]"
            }`}
            data-testid="catalog-new-kind-exotic"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold flex items-center gap-1">
              <Sparkle size={12} weight="duotone" className="text-gold" /> Exotic
            </div>
            <div className="text-xs mt-0.5 text-[#6B6B6B]">
              Rare or less-commonly-registered breed.
            </div>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Name *</Label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={draft.exotic ? "e.g. Karakachan" : "e.g. Black Russian Terrier"}
              className="mt-1.5"
              data-testid="catalog-new-name"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Group</Label>
            <Select
              value={draft.group || undefined}
              onValueChange={(v) => setDraft((d) => ({ ...d, group: v }))}
            >
              <SelectTrigger className="mt-1.5" data-testid="catalog-new-group">
                <SelectValue placeholder={draft.exotic ? "Exotic" : "Pick a group"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Sporting">Sporting</SelectItem>
                <SelectItem value="Hound">Hound</SelectItem>
                <SelectItem value="Working">Working</SelectItem>
                <SelectItem value="Terrier">Terrier</SelectItem>
                <SelectItem value="Toy">Toy</SelectItem>
                <SelectItem value="Non-Sporting">Non-Sporting</SelectItem>
                <SelectItem value="Herding">Herding</SelectItem>
                <SelectItem value="Misc">Miscellaneous</SelectItem>
                <SelectItem value="Foundation">Foundation Stock</SelectItem>
                <SelectItem value="Exotic">Exotic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Origin</Label>
            <Input
              value={draft.origin}
              onChange={(e) => setDraft((d) => ({ ...d, origin: e.target.value }))}
              placeholder={draft.exotic ? "e.g. Bulgaria" : "e.g. United States"}
              className="mt-1.5"
              data-testid="catalog-new-origin"
            />
          </div>
          <div className="md:col-span-1" />
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Description</Label>
            <Textarea
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="One short sentence — origin, purpose, distinctive trait."
              className="mt-1.5 min-h-[64px]"
              data-testid="catalog-new-description"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            onClick={create}
            disabled={creating || !draft.name.trim()}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="catalog-add-submit"
          >
            <Plus size={14} className="mr-1.5" weight="bold" />
            {creating ? "Adding…" : draft.exotic ? "Add exotic breed" : "Add purebred"}
          </Button>
        </div>
      </section>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-5 max-w-2xl">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name"
            className="pl-9"
            data-testid="catalog-search"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={exoticOnly}
            onChange={(e) => setExoticOnly(e.target.checked)}
            className="accent-[#C9A227]"
            data-testid="catalog-exotic-toggle"
          />
          Exotic only
        </label>
        <span className="text-xs text-[#6B6B6B]">{filtered.length} of {rows.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1.4fr_0.9fr_1fr_2fr_120px] gap-3 px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] border-b border-[#E5E5E5] bg-[#FAFAFA]">
          <div>Name</div>
          <div>Group</div>
          <div>Origin</div>
          <div>Description</div>
          <div className="text-right">Actions</div>
        </div>
        {loading && <div className="p-6 text-sm text-[#6B6B6B]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-sm text-[#6B6B6B] text-center">No breeds match your filters.</div>
        )}
        {filtered.map((r) => {
          const isEditing = editId === r.id;
          return (
            <div
              key={r.id}
              className="grid grid-cols-[1.4fr_0.9fr_1fr_2fr_120px] gap-3 px-4 py-3 border-b border-[#E5E5E5] last:border-b-0 items-center text-sm"
              data-testid={`catalog-row-${r.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{r.name}</span>
                {r.exotic && (
                  <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#C9A227]/15 text-[#8a6f10] rounded px-1.5 py-0.5 flex-shrink-0">
                    Exotic
                  </span>
                )}
                {r.is_seed && (
                  <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#0E0E0E] text-white rounded px-1.5 py-0.5 flex-shrink-0">
                    Seed
                  </span>
                )}
              </div>
              {isEditing ? (
                <Input
                  value={editDraft.group || ""}
                  onChange={(e) => setEditDraft((d) => ({ ...d, group: e.target.value }))}
                  className="h-8 text-xs"
                />
              ) : (
                <div className="text-xs text-[#3F3F3F] truncate">{r.group || "—"}</div>
              )}
              {isEditing ? (
                <Input
                  value={editDraft.origin || ""}
                  onChange={(e) => setEditDraft((d) => ({ ...d, origin: e.target.value }))}
                  className="h-8 text-xs"
                />
              ) : (
                <div className="text-xs text-[#3F3F3F] truncate">{r.origin || "—"}</div>
              )}
              {isEditing ? (
                <div className="flex flex-col gap-1.5">
                  <Input
                    value={editDraft.description || ""}
                    onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                    className="h-8 text-xs"
                  />
                  <label className="flex items-center gap-1 text-[11px]">
                    <input
                      type="checkbox"
                      checked={!!editDraft.exotic}
                      onChange={(e) => setEditDraft((d) => ({ ...d, exotic: e.target.checked }))}
                      className="accent-[#C9A227]"
                    />
                    Mark exotic
                  </label>
                </div>
              ) : (
                <div className="text-xs text-[#6B6B6B] truncate" title={r.description}>
                  {r.description || "—"}
                </div>
              )}
              <div className="flex justify-end gap-1.5">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={() => saveEdit(r.id)}
                      disabled={savingId === r.id}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md bg-[#0E0E0E] text-gold hover:bg-[#C1272D] hover:text-white"
                      data-testid={`catalog-save-${r.id}`}
                    >
                      <Check size={14} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditId(null)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[#E5E5E5] text-[#6B6B6B] hover:text-ink"
                    >
                      <CloseIcon size={14} weight="bold" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => startEdit(r)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-[#E5E5E5] text-[#6B6B6B] hover:text-ink hover:border-[#0E0E0E]"
                      data-testid={`catalog-edit-${r.id}`}
                    >
                      <PencilSimple size={14} weight="duotone" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(r)}
                      disabled={r.is_seed}
                      title={r.is_seed ? "Seed breeds can't be deleted" : "Remove"}
                      className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${
                        r.is_seed
                          ? "border-[#E5E5E5] text-[#C5C5C5] cursor-not-allowed"
                          : "border-[#E5E5E5] text-[#C1272D] hover:bg-[#C1272D]/5 hover:border-[#C1272D]/40"
                      }`}
                      data-testid={`catalog-delete-${r.id}`}
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
